"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteSidebar } from "@/components/SiteSidebar";
import { api, loadTokens, type TicketCategory } from "@/lib/api";

/**
 * EPIC-024 STORY-661 — user help-desk submission form.
 *
 * Logged-in users only today; anonymous (pre-signup) submission is
 * STORY-670 phase 2. Logged-out visitors → /login?next=/help/new.
 *
 * Backend enforces: subject 1–200 chars, body 10–10000 chars, valid
 * category. Optional contact_email overrides the JWT user's email
 * for this thread.
 */
// Category enum values are stable; user-visible label/hint flow
// through t() from the helpNew.categories namespace.
const CATEGORY_VALUES: TicketCategory[] = [
  "account",
  "trading",
  "payment",
  "kyc",
  "bug",
  "feature_request",
  "other",
];

const CATEGORY_KEY: Record<TicketCategory, { label: string; hint: string }> = {
  account: {
    label: "helpNew.categories.account",
    hint: "helpNew.categories.accountHint",
  },
  trading: {
    label: "helpNew.categories.trading",
    hint: "helpNew.categories.tradingHint",
  },
  payment: {
    label: "helpNew.categories.payment",
    hint: "helpNew.categories.paymentHint",
  },
  kyc: {
    label: "helpNew.categories.kyc",
    hint: "helpNew.categories.kycHint",
  },
  bug: {
    label: "helpNew.categories.bug",
    hint: "helpNew.categories.bugHint",
  },
  feature_request: {
    label: "helpNew.categories.featureRequest",
    hint: "helpNew.categories.featureRequestHint",
  },
  other: {
    label: "helpNew.categories.other",
    hint: "helpNew.categories.otherHint",
  },
};

export default function NewHelpTicketPage() {
  const { t } = useTranslation();
  const router = useRouter();
  const [authChecked, setAuthChecked] = useState(false);
  const [category, setCategory] = useState<TicketCategory>("other");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!loadTokens()) {
      router.replace("/login?next=" + encodeURIComponent("/help/new"));
      return;
    }
    // Single-shot auth gate; setState here gates the form render but
    // doesn't cascade.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setAuthChecked(true);
  }, [router]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (subject.trim().length === 0) {
      setError(t("helpNew.errSubjectEmpty"));
      return;
    }
    if (body.trim().length < 10) {
      setError(t("helpNew.errBodyShort"));
      return;
    }
    setSubmitting(true);
    try {
      const ticket = await api.submitMyTicket({
        category,
        subject: subject.trim(),
        body: body.trim(),
        contactEmail: contactEmail.trim() || undefined,
      });
      router.replace(`/help/mine/${ticket.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("helpNew.errSubmit"));
      setSubmitting(false);
    }
  }

  if (!authChecked) {
    return (
      <>
        <SiteHeader />
        <main className="mx-auto max-w-md px-6 py-12">
          <p className="text-sm text-[var(--color-text3)]">
            {t("helpNew.loading")}
          </p>
        </main>
      </>
    );
  }

  return (
    <>
      <SiteHeader />
      <div className="mx-auto grid w-full max-w-[1400px] grid-cols-1 gap-5 px-5 py-4 md:grid-cols-[200px_1fr]">
        <SiteSidebar />
        <main className="min-w-0">
          <div className="mb-3 flex items-center justify-between">
            <Link
              href="/help/mine"
              className="text-[12px] text-[var(--color-text3)] hover:text-[var(--color-text)]"
            >
              {t("helpNew.backLink")}
            </Link>
          </div>
          <div
            className="card mx-auto max-w-2xl p-6"
            style={{ background: "var(--color-surface)" }}
          >
            <h1 className="text-2xl font-bold tracking-tight">
              {t("helpNew.title")}
            </h1>
            <p className="mt-1 mb-5 text-[12px] text-[var(--color-text3)]">
              {t("helpNew.subtitle")}
            </p>

            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-bold uppercase tracking-[0.4px] text-[var(--color-text3)]">
                  {t("helpNew.categoryLabel")}
                </label>
                <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                  {CATEGORY_VALUES.map((value) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setCategory(value)}
                      className="rounded-md border px-3 py-2 text-left text-[12px] transition-colors"
                      style={{
                        borderColor:
                          category === value
                            ? "var(--color-brand)"
                            : "var(--color-border)",
                        background:
                          category === value
                            ? "var(--color-brand-tint)"
                            : "var(--color-bg)",
                      }}
                    >
                      <div className="font-semibold">
                        {t(CATEGORY_KEY[value].label)}
                      </div>
                      <div className="text-[10px] text-[var(--color-text3)]">
                        {t(CATEGORY_KEY[value].hint)}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <label className="flex flex-col gap-1.5">
                <span className="text-[11px] font-bold uppercase tracking-[0.4px] text-[var(--color-text3)]">
                  {t("helpNew.subjectLabel")}
                </span>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  maxLength={200}
                  required
                  placeholder={t("helpNew.subjectPlaceholder")}
                  className="h-10 rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-3 text-[13px] outline-none focus:border-[var(--color-brand)]"
                />
              </label>

              <label className="flex flex-col gap-1.5">
                <span className="text-[11px] font-bold uppercase tracking-[0.4px] text-[var(--color-text3)]">
                  {t("helpNew.detailsLabel")}
                </span>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={8}
                  minLength={10}
                  maxLength={10000}
                  required
                  placeholder={t("helpNew.detailsPlaceholder")}
                  className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] p-3 text-[13px] outline-none focus:border-[var(--color-brand)]"
                />
                <span className="text-[10px] text-[var(--color-text3)]">
                  {t("helpNew.detailsCount", { n: body.length })}
                </span>
              </label>

              <label className="flex flex-col gap-1.5">
                <span className="text-[11px] font-bold uppercase tracking-[0.4px] text-[var(--color-text3)]">
                  {t("helpNew.contactEmailLabel")}
                </span>
                <input
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  placeholder={t("helpNew.contactEmailPlaceholder")}
                  className="h-10 rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-3 text-[13px] outline-none focus:border-[var(--color-brand)]"
                />
              </label>

              {error && (
                <div
                  role="alert"
                  className="rounded-md border px-3 py-2 text-[12px]"
                  style={{
                    borderColor:
                      "color-mix(in srgb, var(--color-status-negative) 30%, transparent)",
                    background:
                      "color-mix(in srgb, var(--color-status-negative) 8%, transparent)",
                    color: "var(--color-status-negative)",
                  }}
                >
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="mt-2 inline-flex h-11 items-center justify-center rounded-md px-6 font-semibold text-white disabled:opacity-50"
                style={{ background: "var(--color-brand)" }}
              >
                {submitting ? t("helpNew.submitting") : t("helpNew.submit")}
              </button>
            </form>
          </div>
        </main>
      </div>
    </>
  );
}
